import {
  Button,
  Drawer,
  Dropdown,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Popover,
  Radio,
  Segmented,
  Slider,
  Space,
  Switch,
  Tag,
  TagProps,
  Tooltip,
  Typography,
} from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { SingleQueryWrapper } from "components/QueryWrapper";
import { T_QueryData } from "types/_utils.types";
import { FREQUENCY_TYPES, LoanSchemaDto } from "dtos/loan.dto";
import DashboardLayout from "layout/DashboardLayout";
import { Fragment, ReactElement, ReactNode, useEffect, useState } from "react";
import { LuRotateCcw } from "react-icons/lu";
import {
  TbArrowBigDownFilled,
  TbArrowBigDownLineFilled,
  TbArrowBigDownLinesFilled,
  TbArrowBigUpFilled,
  TbArrowBigUpLineFilled,
  TbArrowBigUpLinesFilled,
  TbDots,
  TbDotsVertical,
  TbHelp,
  TbLetterU,
  TbMinus,
  TbNotebook,
  TbPlayerPlayFilled,
  TbPlus,
  TbX,
} from "react-icons/tb";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  AggressionLevel,
  calculateNegotiationBudget,
  changeACreditorWeightage,
  changeAFinalSuggested,
  changeAFundedAmount,
  changeALoanBalance,
  changeAnAggressionLevel,
  changeANegotiateAs,
  changeANotes,
  changeAOriginalDueAmount,
  changeAPaybackAmount,
  changeAPriority,
  changeAPriorityFixedAmount,
  changeASelfCollected,
  changeAStartWeek,
  changeNegotiationBudget,
  changeTotalWeeklyBudget,
  changeUCCScore,
  loanIntelligenceInit,
  LoanIntelligencePriority,
  NegotiateAs,
  NegotiationStatus,
  removeUCCScore,
  setMonthlyRevenue,
  setPercentageBudget,
  setPercentageCommission,
  toggleCalculationMode,
  RISK_LEVELS,
  T_LoanIntelligence,
  T_LoanIntelligenceLoan,
} from "store/loanIntelligence";
import { calculateAPR, calculateIRR, formatMoney, moneyFormatter, moneyParser, toTitleCase } from "utils/functions";

import TextNumeric from "components/TextNumeric";
import { PercentageNumberInput } from "components/LoanIntelligence/PercentageNumberInput";
import ExternalCalculatorLayout from "layout/ExternalCalculatorLayout/ExternalCalculatorLayout";
import { queryClient } from "utils/queryClient";
import { storedTokens } from "utils/storedTokens";
import { PaymentLedgerModal } from "./CalculatorExecute";
import styles from "./LoanIntelligence.module.scss";
import RichTextEditor from "components/RichTextEditor";
import { StrategyType } from "dtos/application.dto";
import { LEDGER_CATEGORY } from "dtos/ledger.dto";
import { FrequencyLabel } from "components/Application/constants";

const NEGOTIATION_TAG_COLORS: Record<NegotiationStatus, TagProps["color"]> = {
  [NegotiationStatus.SUCCESS]: "green-inverse",
  [NegotiationStatus.ALTERNATE]: "cyan",
  [NegotiationStatus.MANUAL_ACCEPT]: "green",
  [NegotiationStatus.MANUAL_OVERRIDE]: "lime",
  [NegotiationStatus.NEGOTIATION_INITIATED]: "blue",
  [NegotiationStatus.RENEGOTIATION]: "purple",
  [NegotiationStatus.ROADBLOCK]: "red",
};

export default function LoanIntelligence({
  Layout = DashboardLayout,
  externalToken,
}: {
  Layout?: (props: { children?: ReactNode; headerLeft?: ReactNode; headerRight?: ReactNode }) => JSX.Element;
  externalToken?: string;
}) {
  const appKey = useParams()?.appKey;
  const dispatch = useAppDispatch();
  const loanIntelligence = useAppSelector((state) => state.loanIntelligence);
  const [calculatorView, setCalculatorView] = useState<"prev" | "new">("new");
  const [isPaymentLedgerOpen, setIsPaymentLedgerOpen] = useState(false);
  const [isPercentmode, setIsPercentMode] = useState(false);

  const fullApplication = ApiService.application.useGetByAppKey(appKey, "loanIntelligence");
  const getUccScore = ApiService.application.useGetUCCScore(appKey);
  const getclearApp = fullApplication?.data?.data?.application;
  const adminApp = fullApplication?.data?.application;
  const paymentTable = ApiService.application.useGetPaymentTable(fullApplication.data?.application._id);

  // useEffect(() => {
  //   if (loanIntelligence.calculationMode === "percentage" && adminApp?.loan_intelligence?.budget) {
  //     dispatch(setMonthlyRevenue(loanIntelligence.monthlyRevenue));
  //   } else {
  //     dispatch(setMonthlyRevenue(getclearApp?.summary.monthly_revenue ?? 0));
  //   }
  // }, [loanIntelligence.calculationMode, getclearApp, loanIntelligence.monthlyRevenue, adminApp]);
  useEffect(() => {
    // Wait until the data we need is actually available
    if (!getclearApp || !adminApp) return;

    if (loanIntelligence.calculationMode === "percentage" && adminApp?.loan_intelligence?.budget) {
      dispatch(setMonthlyRevenue(loanIntelligence.monthlyRevenue));
    } else {
      dispatch(setMonthlyRevenue(getclearApp?.summary?.monthly_revenue ?? 0));
    }
  }, [loanIntelligence.calculationMode, getclearApp, loanIntelligence.monthlyRevenue, adminApp]);

  //check that every loans has receivables or not
  const allLoansHaveReceivables = fullApplication?.data?.data?.application?.loans?.every(
    (loan) => typeof loan.receivables === "number",
  );

  const isMonthly =
    paymentTable.data?.payment_table.some((entry) => entry.category === LEDGER_CATEGORY.MONTHLY) ?? false;

  return (
    <Layout
      headerRight={
        getclearApp ? (
          <>
            <div className="inline-flex items-center gap-2">
              <Button
                onClick={() => {
                  setIsPaymentLedgerOpen(true);
                }}
              >
                Payment Ledger
              </Button>
              <span>Design</span>
              <Segmented
                options={[
                  { label: "Old", value: "prev" },
                  { label: "New", value: "new" },
                ]}
                value={calculatorView}
                onChange={setCalculatorView}
              />
              {/* <Button
                onClick={() => {
                  if (!allLoansHaveReceivables) {
                    return message.error("All loans must have receivables to toggle calculation mode");
                  }
                  dispatch(toggleCalculationMode());
                }}
              >
                Mode: {loanIntelligence.calculationMode === "percentage" ? "%" : "$"}
              </Button> */}
            </div>
            <Button
              icon={<LuRotateCcw />}
              onClick={() => {
                dispatch(
                  loanIntelligenceInit({
                    app_key: getclearApp?.app_key,
                    loans: getclearApp?.loans,
                    commissionPercentage: (getclearApp?.strategy?.interest_rate ?? 0.22) * 100,
                    totalWeeklyBudget: getclearApp?.strategy?.weekly_payments ?? 0,
                    negotiationBudget: adminApp?.payment_table?.[0]?.repeat_rows?.[0]?.row?.creditor ?? 0,
                    monthlyRevenue: getclearApp.summary.monthly_revenue ?? 0,
                    calculationMode:
                      getclearApp.strategy_type && getclearApp.strategy_type === StrategyType.PERCENTAGE
                        ? "percentage"
                        : "number",
                  }),
                );
              }}
            >
              Reset
            </Button>
          </>
        ) : null
      }
    >
      <PageTitle
        title="Calculator"
        pageTitle={[
          { title: `Applications`, link: "/dashboard/applications" },
          {
            title: fullApplication?.data?.data?.application?.client_details?.name ?? "Application Item",
            link: `/dashboard/application/${fullApplication?.data?.application?.app_key}`,
          },
          { title: "Calculator" },
        ]}
      />
      <SingleQueryWrapper
        query={fullApplication}
        renderData={(application) => {
          return (
            <>
              <LoanIntelligenceInternal
                cycle={isMonthly ? "monthly" : "weekly"}
                application={application}
                intelUccScore={getUccScore?.data?.result?.ucc_score}
                externalToken={externalToken}
                calculatorView={calculatorView}
                isPercentmode={isPercentmode}
                togglePercentMode={() => {
                  setIsPercentMode(!isPercentmode);
                }}
              />
              <PaymentLedgerModal
                open={isPaymentLedgerOpen}
                onClose={() => {
                  setIsPaymentLedgerOpen(false);
                }}
                applicationId={application?.application?._id}
                loans={application?.data?.application?.loans}
              />
            </>
          );
        }}
      />
    </Layout>
  );
}

export function ExternalLoanIntelligence() {
  const [searchParams] = useSearchParams();
  const token = searchParams?.get("token");

  useEffect(() => {
    if (token) {
      storedTokens.accessToken.setToken(token);
    }
  }, [token]);

  return <LoanIntelligence Layout={ExternalCalculatorLayout} externalToken={token ?? undefined} />;
}

const bgColors = [
  "bg-red-100",
  "bg-orange-100",
  "bg-amber-100",
  "bg-yellow-100",
  "bg-lime-100",
  "bg-green-100",
  "bg-emerald-100",
  "bg-teal-100",
  "bg-cyan-100",
  "bg-sky-100",
  "bg-blue-100",
  "bg-indigo-100",
  "bg-violet-100",
  "bg-purple-100",
  "bg-fuchsia-100",
  "bg-pink-100",
  "bg-rose-100",
];

const helpBtn = (
  <Button
    size="small"
    shape="circle"
    variant="filled"
    color="default"
    className="[&>.ant-btn-icon]:flex text-gray-400 hover:text-gray-500"
    icon={<TbHelp />}
  />
);

function LoanIntelligenceInternal({
  application,
  intelUccScore,
  externalToken,
  calculatorView,
  isPercentmode,
  togglePercentMode,
  cycle = "weekly",
}: Readonly<{
  application: T_QueryData<typeof ApiService.application.useGetByAppKey>;
  intelUccScore: number | undefined;
  externalToken?: string;
  calculatorView: "new" | "prev";
  isPercentmode: boolean;
  togglePercentMode: () => void;
  cycle?: "weekly" | "monthly";
}>) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loanIntelligence = useAppSelector((state) => state.loanIntelligence);
  const [uccScore, setUccScore] = useState<number | undefined>(
    typeof intelUccScore === "number" && intelUccScore > 0 ? Math.round(intelUccScore) : undefined,
  );

  const saveLoanIntelligencedata = ApiService.application.useSaveLoanIntelligence(cycle);
  const startCreditorIntel = ApiService.application.useGetCreditorIntel();

  const { columns, dataSource, drawer } = useColumnsAndData(
    loanIntelligence,
    application?.data?.application?.loans.map((loan) => ({
      ...loan,
      attachments: loan?.attachments?.map((el) => el._id),
    })),
    startCreditorIntel?.data,
    application?.data?.application?.strategy?.tenure ?? 0,
    application?.application?._id,
    cycle,
  );

  function applyUcc(val: number | undefined) {
    const newUccScore = val ?? 0;
    dispatch(
      changeUCCScore({
        uccScore: newUccScore,
        loanOriginal: loanIntelligence.loans?.original ?? loanIntelligence.loans.current,
      }),
    );
  }

  function checkCreditorWeightage() {
    const totalCreditors = Object.keys(loanIntelligence.creditorWeightage).length;
    const sortedWeightages = Object.values(loanIntelligence.creditorWeightage).sort((a, b) => b - a);
    const topHalfWeightages = sortedWeightages.slice(0, Math.ceil(totalCreditors / 2));
    const totalWeightage = topHalfWeightages.reduce((sum, weightage) => sum + weightage, 0);
    return totalWeightage >= 95;
  }

  const handleSaveData = () => {
    console.log({ loanIntelligence, actor: "LoanIntelligenceInternal" });

    saveLoanIntelligencedata.mutate(loanIntelligence, {
      onSuccess: () => {
        message.success("Data saved successfully");
      },
    });
  };

  const getColumnContent = (columnKey: string, record: T_LoanRecord, rowIndex: number) => {
    const column = columns.find((col) => col.key === columnKey);
    if (!column) return null;

    if (column.render) {
      return column.render(column.dataIndex ? record[column.dataIndex] : undefined, record, rowIndex);
    } else if (column.dataIndex) {
      return record[column.dataIndex]?.toString();
    }
    return null;
  };

  return (
    <>
      <div className="grid grid-cols-[1fr_350px]">
        {calculatorView === "prev" ? (
          <div className="h-[calc(100vh-100px)] overflow-y-auto flex flex-col gap-6">
            <table className={styles.main_table}>
              <thead>
                <tr>
                  <th>Sl.</th>
                  {columns.map((col) => {
                    return (
                      <th key={col.key} style={{ width: col?.width }}>
                        {col.title}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {application?.data?.application?.loans?.map((loan, loanIndex) => {
                  const thisLoanData = dataSource?.[loan?._id];
                  return (
                    <Fragment key={loan._id}>
                      <tr>
                        <td className={`${bgColors[loanIndex % bgColors.length]} text-black/70 font-medium text-right`}>
                          {loanIndex + 1}.
                        </td>
                        <td
                          colSpan={columns?.length}
                          className={`${bgColors[loanIndex % bgColors.length]} text-black/70 font-medium`}
                        >
                          {loan?.creditor_name}
                        </td>
                      </tr>
                      {thisLoanData?.map((row, rowIndex) => {
                        const isOriginalRow = thisLoanData.length !== 1 && rowIndex !== thisLoanData.length - 1;

                        return (
                          <tr key={row.index}>
                            <td className={isOriginalRow ? "bg-gray-100" : ""}>
                              {rowIndex === 1 ? (
                                <Tooltip title="After applying UCC">
                                  <Button
                                    size="small"
                                    shape="circle"
                                    variant="filled"
                                    color="default"
                                    className="[&>.ant-btn-icon]:flex text-gray-400 hover:text-gray-500"
                                    icon={<TbLetterU />}
                                  />
                                </Tooltip>
                              ) : (
                                ""
                              )}
                            </td>
                            {columns.map((col) => {
                              let display: ReactNode = null;
                              if (col?.render) {
                                display = col.render(col?.dataIndex ? row?.[col?.dataIndex] : undefined, row, rowIndex);
                              } else if (col?.dataIndex) {
                                display = row?.[col?.dataIndex]?.toString();
                              }
                              return (
                                <td
                                  key={col.key}
                                  style={{ width: col?.width }}
                                  className={isOriginalRow ? "bg-gray-100" : ""}
                                >
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {loanIndex === application?.data?.application?.loans?.length - 1 ? null : (
                        <tr>
                          <td colSpan={columns?.length + 1} className="!bg-white !h-4 "></td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-[calc(100vh-100px)] overflow-y-auto p-4 bg-gray-50">
            <div className="space-y-4">
              {application?.data?.application?.loans?.map((loan, loanIndex) => {
                const thisLoanData = dataSource?.[loan?._id];
                if (!thisLoanData) return null;

                // Get the current (editable) row data
                const currentRecord = thisLoanData[thisLoanData.length - 1];
                const originalRecord = thisLoanData.length > 1 ? thisLoanData[0] : null;

                const currBreakevenAmount = getBreakevenAmount(
                  currentRecord?.funded_amount,
                  currentRecord?.payback_amount,
                  currentRecord?.due_amount,
                );
                const originalBreakevenAmount = getBreakevenAmount(
                  originalRecord?.funded_amount,
                  originalRecord?.payback_amount,
                  originalRecord?.due_amount,
                );
                // (currentRecord?.due_amount ?? 0) -
                // ((currentRecord?.payback_amount ?? 0) - (currentRecord?.funded_amount ?? 0));

                return (
                  <div
                    key={loan._id}
                    className="bg-white border border-solid border-gray-300 rounded-lg shadow-md shadow-gray-200"
                  >
                    {/* Header */}
                    <div
                      className={` px-4 py-3 border-t-0 border-l-0 border-r-0  border-b border-solid border-gray-200`}
                    >
                      <div className="flex items-center justify-between ">
                        <div className="inline-flex gap-3 items-center">
                          <h3 className="text-lg font-medium text-gray-900">
                            {loanIndex + 1}. {loan?.creditor_name}
                          </h3>
                          {currentRecord?.negotiation_status && (
                            <Tag color={NEGOTIATION_TAG_COLORS[currentRecord.negotiation_status]}>
                              {currentRecord.negotiation_status.replace("_", " ").toUpperCase()}
                            </Tag>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {getColumnContent("action", currentRecord, thisLoanData.length - 1)}
                        </div>
                      </div>
                    </div>

                    <div className="">
                      <div className="grid grid-cols-1 xl:grid-cols-[1fr,1fr] gap-6 p-4">
                        {/* Left Column */}
                        <div className="grid grid-rows-[repeat(5,24px)] gap-2">
                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Funded Amount</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-end flex items-center bg-gray-100 h-6 ">
                                {getColumnContent("funded_amount", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div className="text-right">
                              {getColumnContent("funded_amount", currentRecord, thisLoanData.length - 1)}
                            </div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Payback Amount</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-end flex items-center bg-gray-100 h-6 ">
                                {getColumnContent("payback_amount", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div className="text-right">
                              {getColumnContent("payback_amount", currentRecord, thisLoanData.length - 1)}
                            </div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Balance Amount</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-end flex items-center bg-gray-100 h-6 ">
                                {getColumnContent("originalDueAmount", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div className="text-right">
                              {getColumnContent("originalDueAmount", currentRecord, thisLoanData.length - 1)}
                            </div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Breakeven Amount</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-end flex items-center bg-gray-100 h-6 ">
                                <Typography.Text>
                                  {originalBreakevenAmount ? formatMoney(originalBreakevenAmount) : "-"}
                                </Typography.Text>
                              </div>
                            ) : null}
                            <div className="text-right">
                              <Typography.Text>
                                {currBreakevenAmount ? formatMoney(currBreakevenAmount) : "-"}
                              </Typography.Text>
                            </div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Settlement Amount</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-end flex items-center bg-gray-100 h-6 ">
                                {getColumnContent("due_amount", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div className="text-right">
                              {getColumnContent("due_amount", currentRecord, thisLoanData.length - 1)}
                            </div>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className="grid grid-rows-[repeat(5,24px)] gap-2">
                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Priority</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-center flex items-center bg-gray-100 h-6 ">
                                {getColumnContent("priority", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div>{getColumnContent("priority", currentRecord, thisLoanData.length - 1)}</div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Aggression</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-center flex items-center bg-gray-100 h-6">
                                {getColumnContent("aggression", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div>{getColumnContent("aggression", currentRecord, thisLoanData.length - 1)}</div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Starting {cycle === "monthly" ? "Month" : "Week"}</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-center flex items-center bg-gray-100 h-6">
                                {getColumnContent("startWeek", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div>{getColumnContent("startWeek", currentRecord, thisLoanData.length - 1)}</div>
                          </div>

                          {/* <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Start Date</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-center flex items-center bg-gray-100 h-6">
                                {getColumnContent("startWeek", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div>{getColumnContent("startWeek", currentRecord, thisLoanData.length - 1)}</div>
                          </div> */}

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Client Paying?</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-center flex items-center bg-gray-100 h-6">
                                {getColumnContent("collectedByCreditor", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div>{getColumnContent("collectedByCreditor", currentRecord, thisLoanData.length - 1)}</div>
                          </div>

                          <div
                            className="grid items-center gap-1"
                            style={{ gridTemplateColumns: `1fr ${originalRecord ? "100px 212px" : "212px"}` }}
                          >
                            <Typography.Text>Negotiate As</Typography.Text>
                            {originalRecord ? (
                              <div className="justify-center flex items-center bg-gray-100 h-6">
                                {getColumnContent("negotiate_as", originalRecord, thisLoanData.length)}
                              </div>
                            ) : null}
                            <div>{getColumnContent("negotiate_as", currentRecord, thisLoanData.length - 1)}</div>
                          </div>
                        </div>

                        {/* Info Section */}
                        {/* <div className="border-t pt-4">
                          <label className="text-sm font-medium text-gray-600 block mb-2">Info</label>
                          <div className="text-sm">
                            {getColumnContent("payments", currentRecord, thisLoanData.length - 1)}
                          </div>
                        </div> */}
                      </div>
                      {/* Payment Table layout */}
                      <div className="border-t border-solid border-b-0 border-x-0 border-gray-200">
                        <table
                          className={`w-full text-sm border-collapse  [&_td]:border-solid [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1 ${styles.main_table}`}
                        >
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 text-gray-700 font-medium">
                                {cycle === "monthly" ? "Monthly" : "Weekly"} Budget{" "}
                                {loanIntelligence.calculationMode === "percentage" ? "(%)" : "($)"}
                              </th>
                              <th className="text-left py-2 text-gray-700 font-medium">
                                Original Payment (
                                {currentRecord?.tenure === Math.round(currentRecord?.tenure ?? 0) ? "" : "~"}
                                {Math.max(1, Math.round(currentRecord?.tenure ?? 0))}{" "}
                                {currentRecord?.frequency ? FrequencyLabel[currentRecord?.frequency] : null})
                              </th>
                              <th className="text-left py-2 text-gray-700 font-medium">
                                Latest {cycle === "monthly" ? "Monthly" : "Weekly"} Offer
                              </th>
                              {/* <th className="text-left py-2 text-gray-700 font-medium">Final Negotiated</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {originalRecord ? (
                              <tr>
                                <td className="py-2 text-center bg-gray-100">
                                  {getColumnContent("suggestedWeeklyPayment", originalRecord, thisLoanData.length)}
                                </td>
                                <td className="py-2 font-medium text-center bg-gray-100">
                                  {originalRecord?.payments ? formatMoney(originalRecord.payments) : "-"}
                                </td>
                                <td className="py-2 text-center bg-gray-100">
                                  {getColumnContent("final_suggested", originalRecord, thisLoanData.length)}
                                </td>
                                {/* <td className="py-2 font-medium text-center bg-gray-100">
                                  {originalRecord?.final_negotiated
                                    ? formatMoney(originalRecord.final_negotiated)
                                    : "-"}
                                </td> */}
                              </tr>
                            ) : null}
                            <tr>
                              <td className="py-2 text-center">
                                {getColumnContent("suggestedWeeklyPayment", currentRecord, thisLoanData.length - 1)}
                              </td>
                              <td className="py-2 font-medium text-center">
                                {currentRecord?.payments ? formatMoney(currentRecord.payments) : "-"}
                              </td>
                              <td className="py-2 text-center">
                                {getColumnContent("final_suggested", currentRecord, thisLoanData.length - 1)}
                              </td>
                              {/* <td className="py-2 font-medium text-center">
                                {currentRecord?.final_negotiated ? formatMoney(currentRecord.final_negotiated) : "-"}
                              </td> */}
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* <div className="border-t py-4 px-4">
                      <div className="flex justify-end space-x-2">
                      </div>
                    </div> */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="h-[calc(100vh-100px)] overflow-y-auto border-0 border-solid border-l border-gray-200 [&>*]:p-4 [&>*]:border-solid [&>*]:border-0 !divide-y !divide-gray-200">
          <div className="flex flex-col gap-3">
            {loanIntelligence.calculationMode === "percentage" && (
              <div className="flex justify-between items-center gap-1">
                <Typography.Text type="secondary">Monthly Revenue</Typography.Text>

                <InputNumber
                  value={loanIntelligence.monthlyRevenue}
                  onChange={(val) => dispatch(setMonthlyRevenue(val ?? 0))}
                />
              </div>
            )}
            <div className="flex justify-between items-center gap-1">
              <Typography.Text type="secondary">Weekly Budget</Typography.Text>
              <PercentageNumberInput
                mode={loanIntelligence.calculationMode}
                value={
                  loanIntelligence.calculationMode === "percentage"
                    ? loanIntelligence.percentageBudget
                    : loanIntelligence.budget.total
                }
                onChange={(val) => {
                  if (loanIntelligence.calculationMode === "percentage") {
                    dispatch(setPercentageBudget((val as number) ?? 0));
                  } else {
                    dispatch(changeTotalWeeklyBudget((val as number) ?? 0));
                  }
                }}
                estimatedDollarAmount={
                  loanIntelligence.calculationMode === "percentage"
                    ? (loanIntelligence.monthlyRevenue / 4) * ((loanIntelligence.percentageBudget ?? 0) / 100)
                    : undefined
                }
                min={0}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center gap-1">
                <Typography.Text type="secondary">Agency Commission</Typography.Text>
                {loanIntelligence.calculationMode === "percentage" ? (
                  <InputNumber
                    size="small"
                    suffix="%"
                    value={loanIntelligence.budget.commissionPercentage}
                    onChange={(val) => {
                      dispatch(setPercentageCommission((val as number) ?? 0));
                    }}
                    formatter={(val) => {
                      if (!val) return "";
                      return `${val}%`;
                    }}
                    parser={(val) => {
                      if (!val) return 0;
                      return parseFloat(val.replace("%", ""));
                    }}
                    className="!w-[140px]"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                    min={0}
                    max={100}
                  />
                ) : (
                  <Typography.Text>
                    <TextNumeric>{formatMoney(loanIntelligence.budget.commissionAmount)}</TextNumeric>
                  </Typography.Text>
                )}
              </div>
              {loanIntelligence.calculationMode === "percentage" && (
                <div className="flex justify-end text-xs text-gray-500">
                  Estimated: {formatMoney(loanIntelligence.budget.commissionAmount)}
                </div>
              )}
            </div>
            <div className="flex justify-between items-center gap-1">
              <Typography.Text type="secondary">Negotiation Budget</Typography.Text>
              <InputNumber
                size="small"
                prefix={isPercentmode ? "%" : "$"}
                formatter={isPercentmode ? undefined : moneyFormatter}
                parser={moneyParser}
                max={isPercentmode ? 100 : loanIntelligence.budget.total}
                value={loanIntelligence.budget.negotiation}
                onChange={(val) => {
                  // checkCreditorWeightage();
                  dispatch(changeNegotiationBudget(val ?? 0));
                }}
                className="!w-[140px]"
                style={{ fontVariantNumeric: "tabular-nums" }}
                min={0}
              />
            </div>
          </div>

          <div>
            <Typography.Text className="text-gray-400 uppercase tracking-wider text-xs font-bold block mb-3">
              Creditor Weightage
            </Typography.Text>
            <div className="flex flex-col gap-3">
              {application?.data?.application?.loans.map((creditor) => {
                if (!(creditor._id in loanIntelligence.creditorWeightage)) return null;
                return (
                  <div className="flex flex-col gap-2" key={creditor._id}>
                    <div className="flex justify-between gap-1">
                      <Typography.Text type="secondary">{creditor?.creditor_name}</Typography.Text>
                      <Typography.Text>
                        <TextNumeric>{loanIntelligence?.creditorWeightage?.[creditor._id]?.toFixed(0)}%</TextNumeric>
                      </Typography.Text>
                    </div>
                    <Slider
                      className="!m-0"
                      min={1}
                      max={99}
                      step={1}
                      value={loanIntelligence.creditorWeightage[creditor._id]}
                      onChange={(val) => {
                        // if (checkCreditorWeightage()) {
                        //   notification.warning({
                        //     message:
                        //       "You have assigned 95% of your weightage to half or less than half of your creditors. Please adjust the weightage before saving.",
                        //   });
                        // }
                        dispatch(changeACreditorWeightage({ loanId: creditor._id, newWeightage: val }));
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="inline-flex gap-1 items-center">
              <Typography.Text type="secondary">UCC Score</Typography.Text>
              <Popover trigger="click" placement="bottomRight" arrow={false} content={<UCCRangeInfoTable />}>
                {helpBtn}
              </Popover>
            </div>
            <div className="inline-flex gap-1">
              <Space.Compact className="w-20">
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={1}
                  value={uccScore ?? null}
                  onChange={(val) => {
                    setUccScore(val ?? undefined);
                  }}
                  controls={false}
                />
                {loanIntelligence?.loans?.original ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setUccScore(undefined);
                      if (loanIntelligence?.loans?.original) {
                        dispatch(removeUCCScore({ loanOriginal: loanIntelligence.loans.original }));
                      }
                    }}
                    icon={<TbX />}
                  />
                ) : null}
              </Space.Compact>

              <Button
                size="small"
                variant="solid"
                color="primary"
                onClick={() => {
                  applyUcc(uccScore);
                }}
              >
                Apply
              </Button>
            </div>
          </div>

          {/* <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center">
              <Typography.Text type="secondary">Creditor Intel</Typography.Text>
              <Button
                size="small"
                variant="solid"
                color="primary"
                onClick={() => {
                  startCreditorIntel.mutate(application?.application?.app_key);
                }}
                loading={startCreditorIntel.isPending}
              >
                Get
              </Button>
            </div>
            {startCreditorIntel?.data ? (
              <div>
                <table className="text-xs border-collapse border-solid border-gray-200 [&_td]:border-solid [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-0.5 w-full">
                  <thead>
                    <tr>
                      <td className="bg-gray-100 font-semibold">Creditor</td>
                      <td className="bg-gray-100 font-semibold">Optimal Settlement</td>
                      <td className="bg-gray-100 font-semibold">Aggression</td>
                    </tr>
                  </thead>
                  <tbody>
                    {startCreditorIntel?.data?.map((thisIntel, i) => {
                      if (!thisIntel) return null;
                      return (
                        <tr key={i}>
                          <td>{thisIntel?.creditor_name}</td>
                          <td className="text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                            {thisIntel?.final_settlement && thisIntel?.final_settlement > 0
                              ? formatMoney(thisIntel?.final_settlement)
                              : "-"}
                          </td>
                          <td>
                            {typeof thisIntel?.aggression_level === "number"
                              ? thisIntel.aggression_level === 0 ||
                                thisIntel.aggression_level < 0 ||
                                thisIntel.aggression_level > 3
                                ? "None"
                                : (
                                    {
                                      1: AggressionLevel.LOW,
                                      2: AggressionLevel.MEDIUM,
                                      3: AggressionLevel.HIGH,
                                    } as any
                                  )[thisIntel.aggression_level]
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div> */}
        </div>
      </div>

      <div className="h-[50px] w-full flex justify-end gap-2 px-4 items-center border-0 border-t border-gray-200 border-solid">
        {checkCreditorWeightage() ? (
          <Popconfirm
            style={{ width: 300 }}
            title="High Weightage Warning"
            description={
              <div className="w-[300px]">
                <Typography.Text>
                  You have assigned 95% of your budget to half or less than half of your creditors. Are you sure you
                  want to save?
                </Typography.Text>
              </div>
            }
            okText="Yes"
            cancelText="No"
            onConfirm={handleSaveData}
            okButtonProps={{ loading: saveLoanIntelligencedata.isPending }}
          >
            <Button type="primary">Save</Button>
          </Popconfirm>
        ) : (
          <Button loading={saveLoanIntelligencedata.isPending} onClick={handleSaveData} type="primary">
            Save
          </Button>
        )}

        {application?.application?.loan_intelligence ? (
          <Button
            onClick={() => {
              if (externalToken) {
                return navigate(
                  `/external/${application?.application?.app_key}/calculator-execute?token=${externalToken}`,
                );
              } else {
                return navigate(`/dashboard/application/${application?.application?.app_key}/calculator-execute`);
              }
            }}
            variant="solid"
            color="green"
            icon={<TbPlayerPlayFilled />}
          >
            Go to Execute
          </Button>
        ) : null}
      </div>
      {drawer}
    </>
  );
}

function UCCRangeInfoTable() {
  return (
    <table className="text-xs border-collapse border-solid border-gray-200 [&_td]:border-solid [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-0.5">
      <thead>
        <tr>
          {Object.keys(RISK_LEVELS[0]).map((col, colIndex) => {
            return (
              <td className="bg-gray-100 font-semibold" key={colIndex}>
                {toTitleCase(col)}
              </td>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {RISK_LEVELS.map((row, rowIndex) => {
          return (
            <tr key={rowIndex}>
              <td>{row.rating}</td>
              <td>{row.range?.join("-")}</td>
              <td>{row.principal === 0 ? "X" : `${row.principal}↓`}</td>
              <td>{row.interest === 0 ? "X" : `${row.interest}↓`}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

type T_AiIntel = T_QueryData<typeof ApiService.application.useGetCreditorIntel>;

function useColumnsAndData(
  loanIntelligence: T_LoanIntelligence,
  appLoans: LoanSchemaDto[],
  aiIntel: T_AiIntel | undefined,
  tenure: number,
  appId: string,
  cycle: "weekly" | "monthly",
) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const negotiateCreditor = ApiService.application.useCalculatorCreditorNegotiate(loanIntelligence.app_key);
  const manualAcceptCreditor = ApiService.application.useCalculatorCreditorManualAccept(loanIntelligence.app_key);
  const manualOverrideCreditor = ApiService.application.useCalculatorCreditorManualOverride(loanIntelligence.app_key);
  const saveLoanIntelligencedata = ApiService.application.useSaveLoanIntelligence(cycle);
  const partialExecute = ApiService.application.usePartialExecute();
  const modifyNumberOfRows = ApiService.ledger.useModifyRows();
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [drawerTitle, setDrawerTitle] = useState("Add/Edit Note");

  const fullData: Record<string, (LoanSchemaDto & T_LoanIntelligenceLoan & { index: number })[]> = {};
  appLoans?.map((loan) => {
    const dataSource: (LoanSchemaDto & T_LoanIntelligenceLoan & { index: number })[] = [];
    if (loanIntelligence?.loans?.original) {
      dataSource.push({
        ...loan,
        ...loanIntelligence.loans.original[loan._id],
        // attachments: loan.attachments.map((el) => el._id),
        index: 0,
      });
    }
    dataSource.push({
      ...loan,
      ...loanIntelligence.loans.current[loan._id],
      // attachments: loan.attachments.map((el) => el._id),
      index: dataSource.length + 1,
    });

    fullData[loan._id] = dataSource;
  });

  const handleSaveData = () => {
    saveLoanIntelligencedata.mutate(loanIntelligence, {
      onSuccess: () => {
        message.success("Data saved successfully");
      },
    });
  };

  const handleRefetch = () => {
    return queryClient.invalidateQueries({
      queryKey: ["application", loanIntelligence.app_key, "loanIntelligence"],
    });
  };

  const handleCreditorPartialExecute = (
    body: NonNullable<ReturnType<typeof ApiService.application.usePartialExecute>["variables"]>,
  ) => {
    partialExecute.mutate(body, {
      onSuccess: (data) => {
        if (data?.code === 201) {
          return message.success("Creditor executed successfully");
        } else {
          if (data?.result?.error?.code === "PL001") {
            return Modal.confirm({
              title: "Creditor payment already exists for some weeks. Are you sure you want to override those?",
              okText: "Confirm",
              onOk: () => {
                handleCreditorPartialExecute({
                  ...body,
                  overwriteExists: true,
                });
              },
            });
          } else if (data?.result?.error?.code === "PL002") {
            return Modal.confirm({
              title: data?.result?.error?.message,
              okText: "Confirm",
              onOk: () => {
                modifyNumberOfRows.mutate(
                  { application_id: appId, count: data?.result?.error?.extras?.weeks ?? 0 },
                  {
                    onSuccess: () => {
                      handleCreditorPartialExecute(body);
                    },
                  },
                );
              },
            });
          }
        }
      },
    });
  };

  const handleOpenNoteDrawer = (loanId: string, currentNote?: string, drawerTitle?: string) => {
    setSelectedLoanId(loanId);
    setNoteText(currentNote || "");
    setDrawerTitle(drawerTitle || "Add/Edit Note");
    setNoteDrawerOpen(true);
  };

  const handleSaveNote = () => {
    if (selectedLoanId) {
      dispatch(changeANotes({ loanId: selectedLoanId, notes: noteText }));
      setNoteDrawerOpen(false);
      message.success("Note Updated");
    }
  };

  const drawer = (
    <Drawer
      title={
        <div className="flex justify-between w-full items-center">
          <Typography.Text>{drawerTitle}</Typography.Text>
          <Button type="primary" onClick={handleSaveNote} icon={<TbPlus />}>
            Add
          </Button>
        </div>
      }
      open={noteDrawerOpen}
      onClose={() => setNoteDrawerOpen(false)}
      width={600}
    >
      <div className="p-6">
        <RichTextEditor value={noteText} onChange={setNoteText} />
      </div>
    </Drawer>
  );

  const columns: {
    key: string;
    dataIndex?: keyof T_LoanRecord;
    width?: number;
    title: ReactNode;
    render: (cell: any, record: T_LoanRecord, rowIndex: number) => ReactNode;
  }[] = [
    {
      key: "funded_amount",
      dataIndex: "funded_amount",
      title: "Funded Amount",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable) {
          return <span>{record?.funded_amount ? formatMoney(record?.funded_amount) : "-"}</span>;
        }
        return (
          <InputNumber
            size="small"
            prefix="$"
            style={{ width: "100%" }}
            formatter={moneyFormatter}
            parser={moneyParser}
            controls={false}
            value={record.funded_amount}
            onChange={(val) => {
              dispatch(
                changeAFundedAmount({
                  loanId: record._id,
                  funded_amount: val ?? undefined,
                }),
              );
            }}
          />
        );
      },
    },
    {
      key: "payback_amount",
      dataIndex: "payback_amount",
      title: "Payback Amount",
      width: 400,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable) {
          return <span>{record?.payback_amount ? formatMoney(record?.payback_amount) : "-"}</span>;
        }
        return (
          <InputNumber
            size="small"
            prefix="$"
            formatter={moneyFormatter}
            style={{ width: "100%" }}
            parser={moneyParser}
            controls={false}
            value={record.payback_amount}
            onChange={(val) => {
              const loan = appLoans?.find((loan) => loan._id === record?._id);
              dispatch(
                changeAPaybackAmount({
                  loanId: record._id,
                  payback_amount: val ?? undefined,
                  alreadyPaidbackAmount: (loan?.payback_amount ?? 0) - (loan?.due_amount ?? 0),
                }),
              );
            }}
          />
        );
      },
    },
    {
      key: "due_amount",
      dataIndex: "due_amount",
      title: "Settlement Amount",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable) {
          return <span>{record?.due_amount ? formatMoney(record?.due_amount) : "-"}</span>;
        }
        return (
          <InputNumber
            size="small"
            prefix="$"
            style={{ width: "100%" }}
            formatter={moneyFormatter}
            parser={moneyParser}
            controls={false}
            value={record.due_amount}
            onChange={(val) => {
              dispatch(
                changeALoanBalance({
                  loanId: record._id,
                  due_amount: val ?? undefined,
                }),
              );
            }}
          />
        );
      },
    },
    {
      title: "Initial Payment",
      key: "payments",
      dataIndex: "payments",
      width: 100,
      render: (_, record) => {
        return record?.payments ? <>{formatMoney(record?.payments)}</> : "-";
      },
    },
    {
      title: "Original Payment",
      key: "tenure",
      dataIndex: "payments",
      width: 100,
      render: (_, record) => {
        return record?.tenure ? (
          <>
            {" "}
            {record?.tenure} {record?.frequency ? FrequencyLabel[record?.frequency] : null}
          </>
        ) : (
          "-"
        );
      },
    },
    {
      key: "info",
      title: "Info",
      width: 400,
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          {record?.funded_amount && record?.payback_amount && record?.payments ? (
            <span>
              <b>APR: </b>
              {calculateAPR(record?.funded_amount, record?.payments, record?.frequency, record?.tenure).toLocaleString(
                "en-US",
                {
                  style: "decimal",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                },
              )}
              %
            </span>
          ) : null}
          {(() => {
            if (record?.funded_amount && record?.payback_amount && record?.payments) {
              const irr =
                calculateIRR(record?.funded_amount, record?.payback_amount, record?.payments, "weekly")?.irr ?? 0;
              return (
                <span>
                  <b>IRR: </b>
                  {irr > 0
                    ? irr.toLocaleString("en-US", {
                        style: "decimal",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })
                    : null}
                  %
                </span>
              );
            }
            return <></>;
          })()}
        </div>
      ),
    },

    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 230,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        const restLength = Object.values(loanIntelligence?.loans?.current)?.length - 1;
        const notNormalLength = Object.values(loanIntelligence?.loans?.current).filter((val) => {
          return val.priority !== LoanIntelligencePriority.NORMAL;
        }).length;

        if (!isEditable) {
          return <span className="text-center block">{loanIntelligence.loans.original?.[record._id].priority}</span>;
        }
        return (
          <Radio.Group
            style={{ width: "100%" }}
            size="small"
            value={loanIntelligence.loans.current?.[record._id]?.priority}
            disabled={
              restLength === notNormalLength &&
              loanIntelligence.loans.current?.[record._id]?.priority === LoanIntelligencePriority.NORMAL
            }
            onChange={(e) => {
              const newPriorityVal = e.target.value as LoanIntelligencePriority;
              dispatch(changeAPriority({ loanId: record._id, priority: newPriorityVal }));
            }}
          >
            <Radio.Button value={LoanIntelligencePriority.NONE}>None</Radio.Button>
            <Radio.Button value={LoanIntelligencePriority.FIXED}>Fixed</Radio.Button>
            <Radio.Button value={LoanIntelligencePriority.NORMAL}>Normal</Radio.Button>
            <Radio.Button value={LoanIntelligencePriority.HIGH}>High</Radio.Button>
          </Radio.Group>
        );
      },
    },
    {
      title: (
        <div className="inline-flex gap-1 items-center">
          <span>Aggression</span>
          <Tooltip trigger="click" title="Only available when Priority High">
            {helpBtn}
          </Tooltip>
        </div>
      ),
      key: "aggression",
      dataIndex: "aggression",
      width: 176,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;

        if (!isEditable) {
          return <>{loanIntelligence.loans.original?.[record._id].aggression ?? "-"}</>;
        } else if (typeof loanIntelligence.loans.current[record._id]?.aggression === "undefined") {
          return "-";
        }
        return (
          <Radio.Group
            size="small"
            style={{ width: "100%" }}
            disabled={typeof loanIntelligence.loans.current[record._id]?.aggression === "undefined"}
            value={loanIntelligence.loans.current[record._id]?.aggression}
            onChange={(e) => {
              const newAggression = e.target.value as AggressionLevel;
              dispatch(changeAnAggressionLevel({ loanId: record._id, aggression: newAggression }));
            }}
          >
            <Radio.Button value={AggressionLevel.LOW}>Low</Radio.Button>
            <Radio.Button value={AggressionLevel.MEDIUM}>Medium</Radio.Button>
            <Radio.Button value={AggressionLevel.HIGH}>High</Radio.Button>
          </Radio.Group>
        );
      },
    },
    {
      title: "Weekly Payments",
      key: "suggestedWeeklyPayment",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        const weeklyRevenue = loanIntelligence.monthlyRevenue / 4;
        const creditorWeightagePercent = loanIntelligence.creditorWeightage[record._id] ?? 0;

        if (!isEditable) {
          const originalSuggested = loanIntelligence.loans.original?.[record._id].suggestedWeeklyPayment;
          if (loanIntelligence.calculationMode === "percentage") {
            return (
              <Tooltip title={`Estimated: ${formatMoney(originalSuggested ?? 0)}`}>
                <span>
                  {`${creditorWeightagePercent.toFixed(1)} (Estimated: ${formatMoney(originalSuggested ?? 0)})`}%
                </span>
              </Tooltip>
            );
          }
          return <span>{originalSuggested ? formatMoney(originalSuggested) : "-"}</span>;
        }

        const thisItemSuggested = loanIntelligence.loans.current[record._id]?.suggestedWeeklyPayment;

        let aiIntelPercent: number;
        let aiIntelIcon: ReactElement | undefined = undefined;
        const aiIntelVal = aiIntel?.find((creditor) => creditor?.creditor_name === record?.creditor_name);
        if (aiIntel && aiIntelVal && thisItemSuggested && aiIntelVal?.final_settlement) {
          if (thisItemSuggested < aiIntelVal?.final_settlement) {
            aiIntelPercent = Math.abs(
              (thisItemSuggested - aiIntelVal?.final_settlement) / aiIntelVal?.final_settlement,
            );
            if (aiIntelPercent <= 5) {
              aiIntelIcon = <TbArrowBigUpLinesFilled className="text-green-600 flex-shrink-0" />;
            } else if (aiIntelPercent > 5 && aiIntelPercent <= 15) {
              aiIntelIcon = <TbArrowBigUpLineFilled className="text-yellow-600 flex-shrink-0" />;
            } else {
              aiIntelIcon = <TbArrowBigUpFilled className="text-red-600 flex-shrink-0" />;
            }
          } else if (thisItemSuggested > aiIntelVal?.final_settlement) {
            aiIntelPercent = Math.abs(
              (thisItemSuggested - aiIntelVal?.final_settlement) / aiIntelVal?.final_settlement,
            );
            if (aiIntelPercent <= 5) {
              aiIntelIcon = <TbArrowBigDownLinesFilled className="text-green-600 flex-shrink-0" />;
            } else if (aiIntelPercent > 5 && aiIntelPercent <= 15) {
              aiIntelIcon = <TbArrowBigDownLineFilled className="text-yellow-600 flex-shrink-0" />;
            } else {
              aiIntelIcon = <TbArrowBigDownFilled className="text-red-600 flex-shrink-0" />;
            }
          }
        }

        if (loanIntelligence.loans.current?.[record._id]?.priority === LoanIntelligencePriority.FIXED) {
          if (loanIntelligence.calculationMode === "percentage") {
            return (
              <Tooltip title={`Estimated: ${formatMoney(thisItemSuggested ?? 0)}`}>
                <InputNumber
                  size="small"
                  style={{ width: "100%" }}
                  suffix="%"
                  formatter={(val) => {
                    if (!val) return "";
                    return `${val}%`;
                  }}
                  parser={(val) => {
                    if (!val) return 0;
                    return parseFloat(val.replace("%", ""));
                  }}
                  // value={creditorWeightagePercent}
                  // value={weeklyRevenue > 0 ? ((thisItemSuggested ?? 0) / weeklyRevenue) * 100 : 0}
                  // onChange={(val) => {
                  //   // Calculate new fixed amount based on percentage input
                  //   const newPercentage = (val as number) ?? creditorWeightagePercent;
                  //   const negotiationBudget = loanIntelligence.budget.negotiation;
                  //   const estimatedDollarAmount = (negotiationBudget * newPercentage) / 100;
                  //   dispatch(changeAPriorityFixedAmount({ loanId: record._id, fixedAmount: estimatedDollarAmount }));
                  // }}
                  value={loanIntelligence.loans.current[record._id]?.fixedWeightagePercent ?? 0}
                  onChange={(val) => {
                    const newPercentage = (val as number) ?? 0;
                    const negotiationBudget = loanIntelligence.budget.negotiation;
                    const estimatedDollarAmount = (negotiationBudget * newPercentage) / 100;
                    dispatch(
                      changeAPriorityFixedAmount({
                        loanId: record._id,
                        fixedAmount: estimatedDollarAmount,
                        fixedPercent: newPercentage, // store what user typed
                      }),
                    );
                  }}
                  min={0}
                  max={100}
                />
              </Tooltip>
            );
          }
          return (
            <InputNumber
              size="small"
              style={{ width: "100%" }}
              prefix="$"
              formatter={moneyFormatter}
              parser={moneyParser}
              value={typeof thisItemSuggested === "number" ? Number(thisItemSuggested.toFixed(2)) : undefined}
              onChange={(val) => {
                const newWeeklyVal = val ?? undefined;
                dispatch(changeAPriorityFixedAmount({ loanId: record._id, fixedAmount: newWeeklyVal }));
              }}
              min={0}
              max={Number(
                calculateNegotiationBudget(
                  loanIntelligence?.budget?.total,
                  loanIntelligence?.budget?.commissionPercentage,
                ).toFixed(2),
              )}
            />
          );
        }

        // Display for NORMAL and HIGH priority
        if (loanIntelligence.calculationMode === "percentage") {
          return (
            <Tooltip title={`Estimated: ${formatMoney(thisItemSuggested ?? 0)}`}>
              <div className="inline-flex gap-1 items-center">
                <span>{`${creditorWeightagePercent.toFixed(1)}% (Est: ${formatMoney(thisItemSuggested ?? 0)})`}</span>
                {aiIntelIcon ?? null}
              </div>
            </Tooltip>
          );
        }

        return (
          <div className="inline-flex gap-1 items-center">
            <span>{thisItemSuggested ? formatMoney(thisItemSuggested) : "-"}</span>
            {aiIntelIcon ?? null}
          </div>
        );
      },
    },
    {
      title: "Start Week",
      key: "startWeek",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable)
          return (
            <span className="text-center block">
              {record?.startWeek ? record.startWeek / (cycle === "monthly" ? 4 : 1) : "-"}
            </span>
          );
        return (
          <Counter loanId={record?._id} value={record?.startWeek} max={tenure} step={cycle === "monthly" ? 4 : 1} />
        );
      },
    },
    {
      title: "Balance Amount",
      key: "originalDueAmount",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable)
          return (
            <span className="text-center block">{record?.originalDueAmount ? record?.originalDueAmount : "-"}</span>
          );
        return (
          <InputNumber
            size="small"
            style={{ width: "100%" }}
            prefix="$"
            formatter={moneyFormatter}
            parser={moneyParser}
            value={
              typeof record?.originalDueAmount === "number" ? Number(record?.originalDueAmount?.toFixed(2)) : undefined
            }
            onChange={(val) => {
              const newVal = val ?? undefined;
              dispatch(changeAOriginalDueAmount({ loanId: record._id, originalDueAmount: newVal }));
            }}
            min={0}
          />
        );
      },
    },
    {
      title: "Client Paying",
      key: "collectedByCreditor",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable) return <span className="text-center block">{record?.collectedByCreditor ? "Yes" : "No"}</span>;
        return (
          <Radio.Group
            block
            style={{ width: 212 }}
            size="small"
            value={record?.collectedByCreditor ? "yes" : "no"}
            onChange={(e) => {
              const newVal = e.target.value === "yes";
              dispatch(changeASelfCollected({ loanId: record._id, selfCollected: newVal }));
            }}
          >
            <Radio.Button value={"yes"}>Yes</Radio.Button>
            <Radio.Button value={"no"}>No</Radio.Button>
          </Radio.Group>
        );
      },
    },
    {
      title: "Negotiate As",
      key: "negotiate_as",
      // width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable) return <span className="text-center block">{record?.negotiate_as ?? "-"}</span>;
        return (
          <Radio.Group
            block
            style={{ width: 212 }}
            size="small"
            value={record?.negotiate_as}
            onChange={(e) => {
              const negotiateAs = e.target.value as NegotiateAs;
              dispatch(changeANegotiateAs({ loanId: record._id, negotiate_as: negotiateAs }));
            }}
          >
            <Radio.Button value={NegotiateAs.CLIENT}>Client</Radio.Button>
            <Radio.Button value={NegotiateAs.FCDS}>FCDS</Radio.Button>
          </Radio.Group>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) => {
        return record?.negotiation_status ? (
          <Tag color={NEGOTIATION_TAG_COLORS[record.negotiation_status]}>
            {record.negotiation_status.replace("_", " ").toUpperCase()}
          </Tag>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Final Negotiated",
      key: "final_negotiated",
      width: 100,
      render: (_, record) => {
        return record?.final_negotiated ? <>{formatMoney(record?.final_negotiated)}</> : "-";
      },
    },
    {
      title: "Final Suggestion",
      key: "final_suggested",
      width: 100,
      render: (_, record, rowIndex) => {
        const isEditable = rowIndex === fullData[record._id].length - 1;
        if (!isEditable)
          return (
            <span className="text-center block">
              {record?.final_suggested ? formatMoney(record?.final_suggested) : "-"}
            </span>
          );
        return (
          <InputNumber
            size="small"
            prefix="$"
            formatter={moneyFormatter}
            parser={moneyParser}
            value={record?.final_suggested}
            onChange={(val) => {
              dispatch(changeAFinalSuggested({ loanId: record._id, final_suggested: val ?? undefined }));
            }}
            min={1}
          />
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 100,
      render: (_, record) => {
        const thisItemFinalSuggested = loanIntelligence.loans.current[record._id]?.final_suggested;

        return (
          <div className="inline-flex  gap-2">
            {record?.final_negotiated ? (
              <Button
                size="small"
                variant="filled"
                color="primary"
                // loading={negotiateCreditor.variables?.creditor_id === record?._id && negotiateCreditor.isPending}
                loading={saveLoanIntelligencedata.isPending}
                onClick={() => {
                  console.log({ loanIntelligence, actor: "useColumnsData" });

                  saveLoanIntelligencedata.mutate(loanIntelligence, {
                    onSuccess: () => {
                      navigate(`/dashboard/application/${loanIntelligence.app_key}/negotiation/${record._id}`);
                      message.success("Data saved successfully");
                    },
                  });
                  // negotiateCreditor.mutate(
                  //   { creditor_id: record._id, suggested_amount: thisItemFinalSuggested },
                  //   {
                  //     onSuccess: () => {
                  //       return handleRefetch();
                  //     },
                  //   },
                  // );
                }}
              >
                Re-negotiate
              </Button>
            ) : (
              <Button
                variant="solid"
                size="small"
                color="primary"
                // loading={negotiateCreditor.variables?.creditor_id === record?._id && negotiateCreditor.isPending}
                loading={saveLoanIntelligencedata.isPending}
                onClick={() => {
                  console.log({ loanIntelligence, actor: "useColumnsData2" });

                  saveLoanIntelligencedata.mutate(loanIntelligence, {
                    onSuccess: () => {
                      navigate(
                        `/dashboard/application/${loanIntelligence.app_key}/negotiation/${record._id}?negotiate_as=${
                          loanIntelligence?.loans?.current?.[record._id]?.negotiate_as === NegotiateAs.FCDS
                            ? "creditor"
                            : "debtor"
                        }`,
                      );
                      message.success("Data saved successfully");
                    },
                  });

                  // negotiateCreditor.mutate(
                  //   { creditor_id: record._id },
                  //   {
                  //     onSuccess: () => {
                  //     },
                  //   },
                  // );
                }}
              >
                Negotiate
              </Button>
            )}
            {record?.negotiation_status === NegotiationStatus.ALTERNATE ? (
              <Button
                size="small"
                variant="solid"
                color="green"
                className="!bg-green-600"
                loading={manualAcceptCreditor.variables?.creditor_id === record?._id && manualAcceptCreditor.isPending}
                onClick={() => {
                  manualAcceptCreditor.mutate(
                    { creditor_id: record._id },
                    {
                      onSuccess: () => {
                        return handleRefetch();
                      },
                    },
                  );
                }}
              >
                Accept
              </Button>
            ) : null}
            {/* {record?.negotiation_status === NegotiationStatus.ALTERNATE ||
            record?.negotiation_status === NegotiationStatus.ROADBLOCK ? ( */}
            {/* <Button
              variant="solid"
              size="small"
              color="default"
              disabled={!thisItemFinalSuggested}
              loading={
                saveLoanIntelligencedata.isPending ||
                (manualOverrideCreditor.variables?.creditor_id === record?._id && manualOverrideCreditor.isPending)
              }
              onClick={() => {
                if (!thisItemFinalSuggested) {
                  return message.error("Enter final suggested amount to proceed");
                }
                saveLoanIntelligencedata.mutate(loanIntelligence, {
                  onSuccess: () => {
                    manualOverrideCreditor.mutate(
                      { creditor_id: record._id, override_amount: thisItemFinalSuggested },

                      {
                        onSuccess: () => {
                          return handleRefetch();
                        },
                      },
                    );
                  },
                });
              }}
            >
              Override
            </Button> */}
            {/* ) : null} */}
            <Button
              variant="solid"
              size="small"
              color="green"
              icon={<TbPlayerPlayFilled />}
              // loading={negotiateCreditor.variables?.creditor_id === record?._id && negotiateCreditor.isPending}
              loading={
                saveLoanIntelligencedata.isPending ||
                (partialExecute.variables?.creditor_id === record?._id && partialExecute?.isPending) ||
                modifyNumberOfRows.isPending
              }
              onClick={() => {
                const startWeek = record?.startWeek;
                const amount = record?.final_suggested ?? record?.suggestedWeeklyPayment;
                if (!startWeek) {
                  return message.error("Start week not provided for this creditor");
                }
                if (!amount) {
                  return message.error("Creditor amount not found.");
                }

                console.log({ loanIntelligence, actor: "useColumnsData3" });
                saveLoanIntelligencedata.mutate(loanIntelligence, {
                  onSuccess: () => {
                    message.success("Data saved successfully");
                    handleCreditorPartialExecute({
                      appkey: loanIntelligence?.app_key,
                      creditor_id: record?._id,
                      start_week: startWeek,
                      weekly_payment: amount,
                    });
                  },
                });
              }}
            >
              Execute
            </Button>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "notes",
                    icon: <TbNotebook />,
                    label: "Add Notes",
                    onClick: () => {
                      handleOpenNoteDrawer(
                        record._id,
                        record?.notes || "",
                        `Notes for ${record?.creditor_name ?? "this creditor"}`,
                      );
                    },
                  },
                ],
              }}
            >
              <Button size="small" icon={<TbDotsVertical />}></Button>
            </Dropdown>
          </div>
        );
      },
    },
  ] as const;

  return { columns, dataSource: fullData, drawer };
}

type T_LoanRecord = LoanSchemaDto & T_LoanIntelligenceLoan & { index: number };

function getBreakevenAmount(
  funded_amount: number | undefined,
  payback_amount: number | undefined,
  due_amount: number | undefined,
) {
  return (due_amount ?? 0) - ((payback_amount ?? 0) - (funded_amount ?? 0));
}

function Counter({
  value,
  max,
  loanId,
  step = 1,
}: Readonly<{ value: number | undefined; max: number; loanId: string; step?: number }>) {
  const dispatch = useAppDispatch();

  const displayValue = value === undefined ? undefined : value / step;
  const displayMax = Math.floor(max / step);

  return (
    <Space.Compact className="w-[212px]">
      <Button
        size="small"
        disabled={displayValue === undefined ? false : displayValue <= 1}
        icon={<TbMinus />}
        onClick={() => {
          if (displayValue !== undefined && displayValue > 1) {
            dispatch(changeAStartWeek({ loanId, startWeek: (displayValue - 1) * step }));
          }
        }}
      />
      <InputNumber
        size="small"
        className="[&_.ant-input-number-input]:text-center"
        style={{ width: "100%" }}
        value={displayValue}
        onChange={(val) => {
          dispatch(
            changeAStartWeek({
              loanId,
              startWeek: val === null ? undefined : val * step,
            }),
          );
        }}
        min={1}
        max={displayMax}
        step={1}
        placeholder="Enter start week"
      />
      <Button
        size="small"
        icon={<TbPlus />}
        disabled={displayValue === undefined ? false : displayValue >= displayMax}
        onClick={() => {
          if (displayValue !== undefined && displayValue < displayMax) {
            dispatch(changeAStartWeek({ loanId, startWeek: (displayValue + 1) * step }));
          }
        }}
      />
    </Space.Compact>
  );
}
